import "../account-management.module.css";

import { useState, useReducer, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Row, Col } from "react-bootstrap";
import { cloneDeep } from "lodash";

// Const
import { organizationType } from "const/nuclias/organization";

// Slice
import { uiActions, selectShowModalSpinner } from "stores/slice/ui";

// UI
import SpinnerForModal from "ui/SpinnerForModal";

// Component
import Button from "components/Button";
import Input from "components/Input";
import ModalContainer from "components/ModalContainer";
import DropdownWithItem from "components/DropdownWithItem";
import DropdownWithCheckbox from "components/DropdownWithCheckbox";
import MessageBoxGroup from "components/MessageBoxGroup";

// Model
import AccountModel from "../models/Account";

// Controller
import controller from "../controller";

// Store
import accountStore from "../stores/account";
import AuthUserService from 'services/Auth.User.service';

const defaultMessages = {
  error: ''
};

const InviteUserModal = props => {
  const {
    modalStatus,
    changeModalStatus,
    user,
    orgTreeMap,
    selectedOrg,
    selectedSiteTag,
    selectedSite,
    successCallback,
    selectedAccount
  } = props;
  const { orgType } = selectedOrg;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const showModalSpinner = useSelector(selectShowModalSpinner);

  // State
  const [messages, setMessages] = useState(cloneDeep(defaultMessages));
  const [accountState, accountDispatch] = useReducer(
    accountStore.reducer,
    accountStore.defaultState
  );

  // Variable
  const managedScopeForSP = (
    <>
      <Col>
        <DropdownWithCheckbox
          id="license-key-dropdown"
          type="checkbox"
          disabled={user?.id === selectedAccount?.id}
          itemList={accountState.managedMSP.list}
          onChange={item => changeManagedMSP(item)}
        />
      </Col>
    </>
  );

  const managedScopeForMSP = (
    <>
      <Col>
        {t(accountState.managedOrganization.value)}
      </Col>
    </>
  );

  const managedForOrg = (
    <>
      <div className="modal-form-field">
        <Row className="mt-2">
          <Col>
            <DropdownWithItem
              id="license-term-dropdown"
              type="normal"
              disabled={user?.id === selectedAccount?.id || accountState.managedSiteTag.disabled}
              selectedItem={accountState.managedSiteTag.selected}
              itemList={accountState.managedSiteTag.list}
              onClick={item => changeMangedSiteTag(item)}
            />
          </Col>
          <Col>
            <DropdownWithItem
              id="license-term-dropdown"
              type="normal"
              disabled={user?.id === selectedAccount?.id || accountState.managedSite.disabled}
              selectedItem={accountState.managedSite.selected}
              itemList={accountState.managedSite.list}
              onClick={item => changeManagedSite(item)}
            />
          </Col>
        </Row>
      </div>
    </>
  );

  // Method
  const init = useCallback(() => {
    if (!selectedAccount) {
      // Invite
      AccountModel.accountState = AccountModel.getInviteAccountState(user, selectedOrg, selectedSiteTag, selectedSite);
    } else {
      // Edit
      AccountModel.accountState = AccountModel.getEditAccountState(user, orgTreeMap, selectedOrg, selectedSiteTag, selectedSite, selectedAccount);
    }

    accountDispatch({
      type: accountStore.action.initializeState,
      payload: AccountModel.accountState
    });
  }, [user, selectedAccount, selectedOrg, selectedSite, selectedSiteTag]);

  const changeName = (value) => {
    const updatedName = cloneDeep(accountState.name);
    AccountModel.changeName(updatedName, value);

    accountDispatch({
      type: accountStore.action.changeName,
      payload: updatedName
    });
  }

  const changeEmail = (value) => {
    const updatedEmail = cloneDeep(accountState.email);
    AccountModel.changeEmail(updatedEmail, value);

    accountDispatch({
      type: accountStore.action.changeEmail,
      payload: updatedEmail
    });
  }

  const changeAccessPrivilegeRole = (role) => {
    const updatedAccessPrivilegeRole = cloneDeep(accountState.accessPrivilegeRole);
    AccountModel.changeAccessPrivilegeRole(updatedAccessPrivilegeRole, role);

    accountDispatch({
      type: accountStore.action.changeAccessPrivilegeRole,
      payload: updatedAccessPrivilegeRole
    });
  }

  const changeManagedMSP = (item) => {
    const updatedManagedMSP = cloneDeep(accountState.managedMSP);
    AccountModel.changeManagedMSP(updatedManagedMSP, item);

    accountDispatch({
      type: accountStore.action.changeManagedMSP,
      payload: updatedManagedMSP
    });
  }

  const changeMangedSiteTag = (item) => {
    const updatedManagedSiteTag = cloneDeep(accountState.managedSiteTag);
    AccountModel.changeManagedSiteTag(updatedManagedSiteTag, item);

    accountDispatch({
      type: accountStore.action.changeManagedSiteTag,
      payload: updatedManagedSiteTag
    });

    const updatedManagedSite = cloneDeep(accountState.managedSite);
    AccountModel.renewManagedSite(orgTreeMap, selectedOrg, updatedManagedSite, item);
    accountDispatch({
      type: accountStore.action.renewManagedSite,
      payload: updatedManagedSite
    });
  }

  const changeManagedSite = (item) => {
    const updatedManagedSite = cloneDeep(accountState.managedSite);
    AccountModel.changeManagedSite(updatedManagedSite, item);

    accountDispatch({
      type: accountStore.action.renewManagedSite,
      payload: updatedManagedSite
    });
  }

  const checkForm = () => {
    let state = true;
    for (const fieldKey in accountState) {
      const { valid } = accountState[fieldKey];
      if (!valid) {
        state = false;
      }
    }

    return state;
  }

  const addOrSave = async () => {
    dispatch(uiActions.showModalSpinner());

    const getEditUser = async () => {
      try {
        const response = await AuthUserService.getUser(selectedAccount.id);

        if (response.error) {
          throw response.error;
        }

        return response.data;
      } catch (error) {
        setMessages({
          ...messages,
          error: error.code,
          success: null,
        });
        dispatch(uiActions.hideModalSpinner());
      }
    }

    let updatedUserData = null;
    if (selectedAccount)
      updatedUserData = await getEditUser();

    try {
      const response = await controller.addOrSaveAccount(accountState, selectedAccount?.id, updatedUserData);

      if (response.error) {
        throw response.error;
      }

      dispatch(uiActions.hideModalSpinner());
      setMessages(cloneDeep(defaultMessages));
      changeModalStatus('inviteAndEdit', false);
      successCallback();
    } catch (error) {
      setMessages({
        ...messages,
        error: error.code,
        success: null,
      });
      dispatch(uiActions.hideModalSpinner());
    }
  }

  // Side effect
  useEffect(() => {
    if (modalStatus.inviteAndEdit.status) {
      dispatch(uiActions.showModalSpinner());
      init();
      setTimeout(() => {
        dispatch(uiActions.hideModalSpinner());
      }, 1000);
    }
  }, [dispatch, init, modalStatus.inviteAndEdit.status]);

  return (
    <>
      <ModalContainer
        modalWidthType="modal-500px"
        openModal={modalStatus.inviteAndEdit.status}
        closeModal={() => {
          setMessages(cloneDeep(defaultMessages));
          changeModalStatus('inviteAndEdit', false);
        }}
      >
        {showModalSpinner && <SpinnerForModal />}
        <div className="header">
          <div className="title">
            {!selectedAccount && t('440a99b2ea')}
            {selectedAccount && t('65d7cc2656')}
          </div>
        </div>

        <div className="body">
          <MessageBoxGroup
            messages={messages}
            ErrorMessagesType='es'
            SuccessMessagesType='i18n'
            WarningMessagesType='i18n'
            onClose={(value) => setMessages({ ...defaultMessages, [value]: null })}
          />
          {/* Name */}
          <div className="mb-2">
            <div className="modal-form-title required">{t('49ee308734')}</div>
            <div className="modal-form-field">
              <Input
                type="text"
                placeholder="7563cde047"
                value={accountState.name.value}
                isInvalid={accountState.name.dirty && !accountState.name.valid}
                onChange={e => changeName(e.target.value)}
              />

            </div>
          </div>
          {/* Email address* */}
          <div className="mb-3">
            <div className="modal-form-title required">{t('b357b524e7')}</div>
            <div className="modal-form-field">
              <Input
                type="text"
                placeholder="ca5977f4f2"
                value={accountState.email.value}
                isInvalid={accountState.email.dirty && !accountState.email.valid}
                onChange={e => changeEmail(e.target.value)}
              />
              <span className='modal-form-prompt'>
                {t('9d537af410')}
              </span>
            </div>
          </div>
          {/* Access privilege */}
          <div className="mb-2">
            <div className="modal-subtitle">{t('8f1bd444d2')}</div>
          </div>
          <div className="mb-2">
            <div className="modal-form-title required">{t('727b677950')}</div>
            <div className="modal-form-field">
              <DropdownWithItem
                id="used-unused-dropdown"
                type="normal"
                disabled={user?.id === selectedAccount?.id || accountState.accessPrivilegeRole.disabled}
                selectedItem={accountState.accessPrivilegeRole.selected}
                itemList={accountState.accessPrivilegeRole.list}
                onClick={changeAccessPrivilegeRole}
              />
            </div>
          </div>
          <div className="mb-3">
            <div className="modal-form-field">
              <Row className="mt-2">
                <Col>
                  <div className="modal-form-title required">{t('de88250cdc')}</div>
                  <DropdownWithItem
                    id="license-term-dropdown"
                    type="normal"
                    disabled={accountState.accessPrivilegeLevelType.disabled}
                    selectedItem={accountState.accessPrivilegeLevelType.selected}
                    itemList={accountState.accessPrivilegeLevelType.list}
                  />
                </Col>
                <Col>
                  <div className="modal-form-title">&nbsp;</div>
                  <DropdownWithItem
                    id="license-term-dropdown"
                    type="normal"
                    disabled={accountState.accessPrivilegeLevelOrg.disabled}
                    selectedItem={accountState.accessPrivilegeLevelOrg.selected}
                    itemList={accountState.accessPrivilegeLevelOrg.list}
                  />
                </Col>
              </Row>
            </div>
          </div>
          {/* Managed scope */}
          <div className="mb-2">
            <div className="modal-subtitle">
              {orgType === organizationType.sp && t('2808d28b05')}
              {orgType === organizationType.si && t('31de740e0b')}
              {orgType !== organizationType.sp && orgType !== organizationType.si && t('9b5dc64484')}
            </div>
          </div>
          <div className="mb-2">
            {orgType === organizationType.sp && managedScopeForSP}
            {orgType === organizationType.si && managedScopeForMSP}
            {orgType !== organizationType.sp && orgType !== organizationType.si && managedForOrg}
          </div>
        </div>

        <div className="footer">
          <Button
            label="4a53b84974"
            className="btn-cancel"
            onClick={() => {
              setMessages(cloneDeep(defaultMessages));
              changeModalStatus('inviteAndEdit', false);
            }}
          />
          <Button
            label="c1a36d6fe6"
            className="btn-submit"
            disabled={!checkForm()}
            onClick={() => addOrSave()}
          />
        </div>
      </ModalContainer>
    </>
  );
}

export default InviteUserModal;
